/**
 * Header — barre supérieure. Itère sur `manifest.headerSlot` des features
 * actives sur le contexte courant.
 *
 * @remarks
 * **N'importe AUCUNE feature directement** — l'anti-pattern legacy
 * (`Header` qui importait `EvaluationDeadlineTimer`) est définitivement fixé.
 *
 * Le Header rend :
 * 1. Le logo (à gauche)
 * 2. Les `headerSlot` agrégés (par contexte) au centre-droit
 * 3. Les `globalHeaderSlot` transverses + les toggles rapides (layout, thème)
 *    et le user menu à droite
 *
 * Le badge de compte à rebours de l'attempt en cours est un `globalHeaderSlot`
 * de la feature tech-tests : le Header ne l'importe pas, il itère dessus.
 */

import { Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PanelLeft, PanelRight } from 'lucide-react';
import { featureRegistry, type AppContext, type Context } from '@/app/registry';
import { AltLogo, ContentLayoutToggle, ThemeToggle } from '@/shared/components';
import { IconButton } from '@/shared/ui';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { GuidedModeToggle } from '@/app/guided-tour/GuidedModeToggle';
import { CommandBar } from './CommandBar';
import { UserMenu } from './UserMenu';

interface Props {
  context: Context;
  appCtx: AppContext;
}

export function Header({ context, appCtx }: Props) {
  // Le raccourci centered/stretched n'a d'utilité que côté admin (tableaux et
  // formulaires larges) — côté utilisateur, le réglage reste accessible via
  // Réglages > Apparence (`ContentLayoutControl`).
  const isAdminRoute = useLocation().pathname.startsWith('/admin');
  const leftDrawerOpen = useLayoutStore((s) => s.leftDrawerOpen);
  const toggleLeftDrawer = useLayoutStore((s) => s.toggleLeftDrawer);
  const rightDrawerOpen = useLayoutStore((s) => s.rightDrawerOpen);
  const toggleRightDrawer = useLayoutStore((s) => s.toggleRightDrawer);

  // `appCtx` est memoizé en amont. On évite 3 appels registry par render du
  // shell quand seul un drawer toggle change (qui déclenche un re-render du Header).
  const slots = useMemo(() => featureRegistry.getHeaderSlots(context, appCtx), [context, appCtx]);
  const globalSlots = useMemo(() => featureRegistry.getGlobalHeaderSlots(appCtx), [appCtx]);
  const hasLeftPanel = useMemo(
    () => featureRegistry.getLeftPanelTabs(context, appCtx).length > 0,
    [context, appCtx],
  );
  const hasRightPanel = useMemo(
    () => featureRegistry.getRightPanelTabs(context, appCtx).length > 0,
    [context, appCtx],
  );

  // Le toggle du panneau droit est là sur toutes les pages qui en ont un. Il
  // était masqué sur les tests et les résultats, au profit de l'ampoule du titre :
  // dans le bandeau commun (SAAS-1346), l'ampoule ne se lisait plus comme un
  // bouton, et le panneau ne se refermait plus nulle part.

  return (
    <header className="border-border bg-surface flex h-12 shrink-0 items-center gap-3 border-b px-3">
      {/* Logo + toggle left panel */}
      <div className="flex shrink-0 items-center gap-3">
        <AltLogo className="mb-1 h-6 w-auto opacity-90 transition-opacity duration-(--transition-fast) hover:opacity-100" />
        {hasLeftPanel ? (
          <IconButton
            data-tour="header-left-panel"
            aria-label="Afficher ou masquer le panneau gauche"
            aria-pressed={leftDrawerOpen}
            size="sm"
            onClick={toggleLeftDrawer}
            title="Afficher ou masquer le panneau gauche"
            className={
              leftDrawerOpen ? 'text-primary bg-(--color-accent-bg)' : 'text-fg-muted hover:text-fg'
            }
          >
            <PanelLeft className="size-4" aria-hidden />
          </IconButton>
        ) : null}
      </div>

      {/* CommandBar — centre */}
      <div className="flex flex-1 items-center justify-center">
        <CommandBar />
      </div>

      {/* Header slots des features actives */}
      <div className="flex shrink-0 items-center gap-1">
        {slots.map((SlotComponent, i) => (
          <Suspense key={i} fallback={null}>
            <SlotComponent />
          </Suspense>
        ))}
      </div>

      {/* Actions globales */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Slots globaux (ex. compte à rebours de l'attempt en cours) — visibles
            sur tous les contextes, s'auto-masquent si rien à afficher. */}
        {globalSlots.map((SlotComponent, i) => (
          <Suspense key={`global-${String(i)}`} fallback={null}>
            <SlotComponent />
          </Suspense>
        ))}
        <GuidedModeToggle />
        {isAdminRoute ? <ContentLayoutToggle /> : null}
        <ThemeToggle />
        {hasRightPanel ? (
          <IconButton
            data-tour="header-right-panel"
            aria-label="Afficher ou masquer le panneau droit"
            aria-pressed={rightDrawerOpen}
            size="sm"
            onClick={toggleRightDrawer}
            title="Afficher ou masquer le panneau droit"
            className={
              rightDrawerOpen
                ? 'text-primary bg-(--color-accent-bg)'
                : 'text-fg-muted hover:text-fg'
            }
          >
            <PanelRight className="size-4" aria-hidden />
          </IconButton>
        ) : null}
        <UserMenu />
      </div>
    </header>
  );
}
